import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

type SuperAdminAuthWrapperProps = {
  children: React.ReactNode;
};

const SuperAdminAuthWrapper = ({ children }: SuperAdminAuthWrapperProps) => {
  const { user, loading, ready } = useAuth();
  const location = useLocation();
  const [isSuperUser, setIsSuperUser] = useState<boolean | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [waitingForSession, setWaitingForSession] = useState(false);

  // Add initial debug log
  useEffect(() => {
    console.log("SuperAdminAuthWrapper: Initial render", { 
      userExists: !!user, 
      loading, 
      ready,
      checkingStatus, 
      isSuperUser, 
      isAuthorized,
      waitingForSession
    });
  }, [user, loading, ready, checkingStatus, isSuperUser, isAuthorized, waitingForSession]);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("SuperAdminAuthWrapper: Auth state changed", { event, userExists: !!session?.user });
      
      // If we're waiting for a session and we get one, stop waiting
      if (waitingForSession && session) {
        console.log("SuperAdminAuthWrapper: Session received after waiting");
        setWaitingForSession(false);
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, [waitingForSession]);

  // Check if the user is a superadmin
  useEffect(() => {
    // Don't proceed if auth isn't ready yet
    if (!ready) {
      console.log("SuperAdminAuthWrapper: Auth not ready yet, waiting...");
      return;
    }

    // If auth is ready but no user yet, wait a bit longer
    if (ready && !user && !waitingForSession) {
      console.log("SuperAdminAuthWrapper: Auth ready but no user yet, waiting for session...");
      setWaitingForSession(true);
      return;
    }

    // Don't proceed if we're still waiting for the session
    if (waitingForSession) {
      console.log("SuperAdminAuthWrapper: Still waiting for session...");
      return;
    }

    const checkSuperUserStatus = async () => {
      console.log("SuperAdminAuthWrapper: Checking super user status", { userExists: !!user, userEmail: user?.email });
      
      if (!user) {
        console.log("SuperAdminAuthWrapper: No user found, setting isSuperUser to false");
        setIsSuperUser(false);
        setCheckingStatus(false);
        return;
      }

      // First check if email matches allowed superadmin emails
      const allowedEmails = [
        'hallryan97@gmail.com',
        'ryan.hall@gtmpact.com',
        'ryan@gtmpact.com'
      ];
      
      console.log("SuperAdminAuthWrapper: Checking if email is in allowed list", { 
        email: user.email, 
        isInList: user.email && allowedEmails.includes(user.email) 
      });
      
      if (user.email && allowedEmails.includes(user.email)) {
        console.log("SuperAdminAuthWrapper: Email is in allowed list, setting isSuperUser to true");
        setIsSuperUser(true);
        setCheckingStatus(false);
        return;
      }

      // If email doesn't match, check for superuser flag in profiles
      try {
        console.log("SuperAdminAuthWrapper: Email not in allowed list, checking database");
        const { data, error } = await supabase
          .from('user_profiles')
          .select('is_superuser')
          .eq('id', user.id)
          .single();

        console.log("SuperAdminAuthWrapper: Database query result", { data, error });

        if (error) {
          console.error('Error checking superuser status:', error);
          setIsSuperUser(false);
        } else {
          console.log("SuperAdminAuthWrapper: Setting isSuperUser based on database result", { isSuperuser: !!data?.is_superuser });
          setIsSuperUser(!!data?.is_superuser);
        }
      } catch (error) {
        console.error('Error in superuser check:', error);
        setIsSuperUser(false);
      }
      
      console.log("SuperAdminAuthWrapper: Finished checking status, setting checkingStatus to false");
      setCheckingStatus(false);
    };

    checkSuperUserStatus();
  }, [user, ready, waitingForSession]);

  // Use a delay to prevent loading flashes
  useEffect(() => {
    console.log("SuperAdminAuthWrapper: Auth status check", { 
      loading, 
      ready,
      checkingStatus, 
      isSuperUser,
      waitingForSession,
      conditionsMet: !loading && !checkingStatus && isSuperUser && !waitingForSession
    });
    
    if (!loading && !checkingStatus && isSuperUser && !waitingForSession) {
      console.log("SuperAdminAuthWrapper: Setting up authorization delay");
      const timer = setTimeout(() => {
        console.log("SuperAdminAuthWrapper: Authorization delay complete, setting isAuthorized to true");
        setIsAuthorized(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loading, checkingStatus, isSuperUser, waitingForSession]);

  // Show loading spinner while checking status
  if (loading || !ready || checkingStatus || !isAuthorized || waitingForSession) {
    console.log("SuperAdminAuthWrapper: Showing loading spinner", { 
      loading, 
      ready, 
      checkingStatus, 
      isAuthorized,
      waitingForSession
    });
    return (
      <div className="fixed inset-0 bg-yellow-50 z-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-600"></div>
          <p className="text-yellow-800 font-medium">Loading SuperAdmin Portal</p>
        </div>
      </div>
    );
  }

  // Redirect non-superadmins to unauthorized page
  if (!isSuperUser) {
    console.log("SuperAdminAuthWrapper: Not a superuser, redirecting to unauthorized page");
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  // User is a superadmin, allow access
  console.log("SuperAdminAuthWrapper: SuperAdmin authenticated, allowing access");
  return <>{children}</>;
};

export default SuperAdminAuthWrapper; 